import { useEffect } from 'react'
import type dayjs from 'dayjs'
import { useDeepEffect } from '@retailer-platform/shared-common'
import { type SimpleOption } from '@retailer-platform/shared-components/src/common/select/utils/select.types'
import { useRetailerId } from '@retailer-platform/dashboard/routing'
import {
  InsightsPortalSchema,
  useGetInsightsPortalFilterOptionsLazyQuery,
  useGetInsightsPortalMetricsQuery,
} from '../../api'
import { useInsightsPortalAuthorizationContext } from '../../utils/authorization-context.hooks'
import { useFilterParamsContext } from '../../utils/filter-params/filterParamsContext'
import { optionsArrayToInFilterSelection } from '../utils/insightsPortalFilterTransforms'
import {
  createFilterParamConfig,
  transformApiOptionsIntoDashboardOptions,
  transformFilterParamsToApiFilterSelectionsFactory,
} from '../utils/insightsPortalSchemas.utils'
import { type FilterParamsState } from '../../utils/filter-params/filterParams.types'
import { calculateComparisonPeriod } from '../../utils/insights-portal.utils'
import { useFormatValue } from '../../utils/format-value.hooks'
import { useDomainMessages } from '../../utils/domain/intl'
import { useInsightsPortalQueryTags } from '../../utils/query-tags.hooks'
import { Metrics } from './insightsPortalAdsSponosoredProduct.types'

export const transformFilterParamsForApiConfig = [
  createFilterParamConfig(
    'storeLocation',
    'RETAILER_LOCATION',
    optionsArrayToInFilterSelection('stringValue')
  ),
  createFilterParamConfig(
    'storeLocationName',
    'RETAILER_LOCATION_NAME',
    optionsArrayToInFilterSelection('stringValue')
  ),
  createFilterParamConfig('retailer', 'RETAILER', optionsArrayToInFilterSelection('stringValue')),
  createFilterParamConfig(
    'demandSource',
    'DEMAND_SOURCE',
    optionsArrayToInFilterSelection('stringValue')
  ),
]

const transformFilterSelectionsToAdsSponsoredProductFilters =
  transformFilterParamsToApiFilterSelectionsFactory(transformFilterParamsForApiConfig)

const schema = InsightsPortalSchema.AdsSponsoredProduct

// ads dashboard needs to always filter on the retailer that is selected in the URL
export const useAdsDashboardFixedFilters = () => {
  const retailerId = useRetailerId()

  return {
    retailer: [{ label: '', value: retailerId }],
  }
}

export const useAdsSponsoredProductsFilters = () => {
  const [getFilters, { data, loading, error }] = useGetInsightsPortalFilterOptionsLazyQuery()

  const { selectedFiltersValues, dates } = useFilterParamsContext()

  const { partnerIds, retailerIds, retailerLocationIds } = useInsightsPortalAuthorizationContext()

  const fixedValues = useAdsDashboardFixedFilters()

  const queryTags = useInsightsPortalQueryTags()

  useDeepEffect(() => {
    // order matters here, fixedValues should be put after selectedFiltersValues
    const filterValues = { ...selectedFiltersValues, ...fixedValues }
    getFilters({
      variables: {
        schema,
        partnerIds,
        retailerIds,
        retailerLocationIds,
        filters: transformFilterSelectionsToAdsSponsoredProductFilters(filterValues, dates),
        queryTags,
      },
    })
  }, [
    selectedFiltersValues,
    fixedValues,
    getFilters,
    dates,
    partnerIds,
    retailerIds,
    retailerLocationIds,
    queryTags,
  ])

  const filterOptions = transformApiOptionsIntoDashboardOptions(
    data?.insightsPortalServiceGetInsightsPortalFilterOptions?.filterOptions,
    {
      RETAILER_LOCATION: 'stringValue',
      DEMAND_SOURCE: 'stringValue',
    }
  )

  return {
    filterOptions,
    loading,
    error,
  }
}

const useAdsSponsoredProductMetrics = (
  selectedFiltersValues: FilterParamsState['selectedFiltersValues'],
  dates: { from: dayjs.Dayjs; to: dayjs.Dayjs },
  metrics: Metrics[]
) => {
  const { partnerIds, retailerIds, retailerLocationIds } = useInsightsPortalAuthorizationContext()

  const from = dates.from.clone().startOf('day')
  const to = dates.to.clone().endOf('day')

  const currentPeriodDateRange = {
    from,
    to,
  }

  const fixedFilters = useAdsDashboardFixedFilters()

  const filterValues = { ...selectedFiltersValues, ...fixedFilters }

  const { data, loading } = useGetInsightsPortalMetricsQuery({
    variables: {
      schema,
      partnerIds,
      retailerIds,
      retailerLocationIds,
      metrics,
      filters: transformFilterSelectionsToAdsSponsoredProductFilters(
        filterValues,
        currentPeriodDateRange
      ),
    },
  })

  return {
    data: data?.insightsPortalServiceGetInsightsPortalMetricValues?.metricValues || [],
    loading,
  }
}

export const useCurrentPeriodAdsSponsoredProductMetrics = (metrics: Metrics[]) => {
  const { selectedFiltersValues, dates } = useFilterParamsContext()

  const from = dates.from.clone().startOf('day')
  const to = dates.to.clone().endOf('day')

  const currentPeriodDateRange = {
    from,
    to,
  }

  return useAdsSponsoredProductMetrics(selectedFiltersValues, currentPeriodDateRange, metrics)
}

export const useLastPeriodAdsSponsoredProductMetrics = (metrics: Metrics[]) => {
  const { selectedFiltersValues, dates } = useFilterParamsContext()

  const from = dates.from.clone().startOf('day')
  const to = dates.to.clone().endOf('day')

  const lastPeriodDateRange = calculateComparisonPeriod(from, to, dates.comparisonType)

  return useAdsSponsoredProductMetrics(selectedFiltersValues, lastPeriodDateRange, metrics)
}

export const useAdsSponsoredProductMetricValueFormatter = () => {
  const { formatCurrency, formatInteger, formatPercentage } = useFormatValue()
  const formatters = [
    { metric: Metrics.CLICKS, formatter: formatInteger, compact: false },
    { metric: Metrics.IMPRESSIONS, formatter: formatInteger, compact: false },
    { metric: Metrics.AVG_CPC, formatter: formatCurrency, compact: false },
    { metric: Metrics.CTR, formatter: formatPercentage, compact: false },
    { metric: Metrics.REV_SHARE, formatter: formatCurrency, compact: true },
  ]

  const formatter = (metric: Metrics, value: number) => {
    const metricFormatter = formatters.find(f => f.metric === metric)

    if (metricFormatter) {
      return metricFormatter.formatter(value, metricFormatter.compact)
    }
    return value.toLocaleString()
  }
  return {
    formatter,
  }
}

export const useAdsSponsoredProductMetricLabels = () =>
  useDomainMessages({
    CLICKS: 'insightsPortalDomain.adsDashboard.metrics.CLICKS',
    IMPRESSIONS: 'insightsPortalDomain.adsDashboard.metrics.IMPRESSIONS',
    AVG_CPC: 'insightsPortalDomain.adsDashboard.metrics.AVG_CPC',
    CTR: 'insightsPortalDomain.adsDashboard.metrics.CTR',
    REV_SHARE: 'insightsPortalDomain.adsDashboard.metrics.REV_SHARE',
  })
